export function getScopeGroupId() {
  return Liferay.ThemeDisplay.getScopeGroupId();
}

/**
 * Liferay OAuth2 and API utilities
 * Shared across all forms in the SCL Forms client extension
 */
class OAuth2 {
  constructor() {
    this.baseURL = this.getPortalURL();
    this._accessToken = null;
    this._expiresAt = 0;
    this._tokenPromise = null; // prevents parallel duplicate calls
  }

  /**
   * Get portal URL - dynamically determine based on environment
   */
  getPortalURL() {
    if (typeof window !== 'undefined' && window.Liferay?.ThemeDisplay) {
      return window.Liferay.ThemeDisplay.getPortalURL();
    }
    return window.location.origin;
  }

  /**
   * Get CSRF token from Liferay
   */
  getCsrfToken() {
    if (typeof window !== 'undefined' && window.Liferay) {
      return window.Liferay.authToken;
    }
    return '';
  }

  /**
   * Check if user is signed in
   */
  isUserSignedIn() {
    if (typeof window !== 'undefined' && window.Liferay?.ThemeDisplay) {
      return window.Liferay.ThemeDisplay.isSignedIn();
    }
    return false;
  }

  /**
   * Get scope group ID from Liferay ThemeDisplay
   */
  getScopeGroupId() {
    if (typeof window !== 'undefined' && window.Liferay?.ThemeDisplay) {
      return window.Liferay.ThemeDisplay.getScopeGroupId();
    }
    return '20117'; // fallback
  }

  /**
   * Get user information
   */
  getUser() {
    const user = { scopeGroupId: this.getScopeGroupId() };
    if (typeof window !== 'undefined' && window.Liferay?.ThemeDisplay) {
      user.userId = window.Liferay.ThemeDisplay.getUserId();
      user.isSignedIn = window.Liferay.ThemeDisplay.isSignedIn();
    }
    return user;
  }

  /**
   * 🔹 Check if cached token still valid
   * keep 30s safety buffer before expiry
   */
  _isTokenValid() {
    return (
      this._accessToken &&
      Date.now() < this._expiresAt - 30000
    );
  }

  /**
   * 🔹 Actually call token service
   */
  async _fetchNewToken() {
    const response = await fetch("/o/tokenService/get-token", {
      method: "GET",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json"
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Token request failed: ${response.status} - ${errorText}`);
    }

    const result = await response.json();

    const token = result?.data?.accessToken || null;

    if (!token) {
      throw new Error("Token service returned no access token");
    }

    // ✅ cache token
    this._accessToken = token;

    // ✅ assume ~8 min validity if not provided by service
    const DEFAULT_TTL_SEC = 8 * 60;
    const expiresIn = result?.data?.expiresIn || DEFAULT_TTL_SEC;

    this._expiresAt = Date.now() + (expiresIn * 1000);

    return this._accessToken;
  }

  /**
   * Get OAuth2 access token using token service (CACHED)
   */
  async getAccessToken() {
    try {
      // ✅ use cached if valid
      if (this._isTokenValid()) {
        return this._accessToken;
      }

      // ✅ if already fetching → reuse same promise
      if (this._tokenPromise) {
        return this._tokenPromise;
      }

      // ✅ fetch once
      this._tokenPromise = this._fetchNewToken()
        .finally(() => {
          this._tokenPromise = null;
        });

      return this._tokenPromise;

    } catch (error) {
      console.error("Error fetching access token:", error);
      this._accessToken = null;
      this._expiresAt = 0;
      return null;
    }
  }

  /**
   * Get standard headers for API requests (including Authorization if available)
   */
  async getHeaders() {
    const token = await this.getAccessToken();

    const headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Get headers for multipart form data
   */
  async getMultipartHeaders() {
    const token = await this.getAccessToken();

    const headers = {};

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }
}

// Singleton instance
const oauth2Instance = new OAuth2();

export default OAuth2;
export { oauth2Instance };
